import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Sidebar } from './Sidebar';
import { cn } from '../../lib/utils';

// AppShell — Design System Phase 3 §4. The full-height Sidebar (logo, grouped
// nav, user menu) replaces the old top header bar entirely. Each page renders
// its own PageHeader + content inside the padded main area.
//
// TWO LAYOUT MODES
//
// 1. DEFAULT (every page): the shell is `min-h-screen` and grows with content —
//    the WINDOW scrolls. This is the historical behaviour and several pages
//    depend on it (window.scrollTo in MetaAdCompose / BuildViewNavBar, the
//    Sidebar's `sticky top-0`), so it is deliberately unchanged.
//
// 2. FULL-HEIGHT (opt-in via useFullHeightLayout): the shell is bounded to
//    exactly ONE VIEWPORT with NO page-level scroll, and `main` stops growing.
//    A page in this mode is responsible for its own internal scrolling — it
//    lays out columns that each scroll independently (`min-h-0 overflow-y-auto`).
//
//    Why this exists: the Community inbox needs a composer pinned to the bottom
//    of the conversation column. That is only possible if NO ancestor grows past
//    the viewport — otherwise the column's "bottom" is the bottom of a very tall
//    PAGE (far below the fold), not the screen. `min-h-screen` here was exactly
//    that unconstrained ancestor: it sets a MINIMUM, so a tall accounts list or
//    comment feed pushed the whole page (and the composer) down. Bounding the
//    shell is what makes per-column scrolling — and a pinned composer — work.
//
//    It is opt-IN (not global) so the default window-scroll pages keep their
//    exact current behaviour.

type AppLayoutContextValue = { setFullHeight: (on: boolean) => void };

const AppLayoutContext = createContext<AppLayoutContextValue | null>(null);

/**
 * Opt the current page into the viewport-bounded shell (mode 2 above) for as
 * long as it is mounted. The page MUST then manage its own scrolling — give it
 * `h-full` and make each scrollable region `min-h-0 overflow-y-auto`, or its
 * content will be clipped (the shell no longer scrolls for it).
 */
export function useFullHeightLayout(): void {
  const ctx = useContext(AppLayoutContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setFullHeight(true);
    return () => ctx.setFullHeight(false);
  }, [ctx]);
}

export function AppShell({ children }: { children: ReactNode }) {
  const [fullHeight, setFullHeight] = useState(false);
  // Stable identity so the opt-in effect doesn't re-fire every render.
  const value = useMemo<AppLayoutContextValue>(() => ({ setFullHeight }), []);

  return (
    <AppLayoutContext.Provider value={value}>
      <div
        className={cn(
          'flex bg-background',
          // dvh (not vh) so mobile browser chrome doesn't overshoot the bound.
          fullHeight ? 'h-[100dvh] overflow-hidden' : 'min-h-screen',
        )}
      >
        <Sidebar />
        <main
          className={cn(
            'min-w-0 flex-1 px-8 py-8',
            // min-h-0 lets main shrink inside the bounded row instead of being
            // sized by its content; overflow-hidden guarantees the page itself
            // never scrolls (the page's own columns do).
            fullHeight && 'min-h-0 overflow-hidden',
          )}
        >
          {children}
        </main>
      </div>
    </AppLayoutContext.Provider>
  );
}
